import geoViewport from '@mapbox/geo-viewport';
import {
  Button,
  Checkbox,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Popover,
  Radio,
  Stack,
  Toggle,
} from '@sixfold/common-ui';
import { getBounds } from '@sixfold/geo-primitives';
import React, { Component } from 'react';

import { TourOverlay } from './tour_overlay';
import { getEmbedConfig } from '../../lib/data';
import { generateRandomHexString } from '../../lib/util/string';
import { openSatelliteViewUrl } from '../../lib/util/url';
import { MapGL } from '../../map/components/map_gl';
import { MapViewport } from '../../map/components/map_viewport';
import { Vehicle, VehicleBreakHistory, VehicleHistory, VehicleStatus } from '../../vehicle/entities';
import { Tour, Ruler, MappableStop, MapboxStyle, getThemedMapStyle } from '../entities';
import { getStopsFromTourStops, calculateDistanceInMeters, getExternalEvents } from '../utils';

const MAPBOX_TILE_SIZE = 512;
const MAP_BOUNDS_PADDING = 50;
const MIN_ZOOM_LEVEL = 5;
const MAX_ZOOM_LEVEL = 20;

export interface TourMapViewDataProps {
  data: {
    tour: Tour;
    vehicles: (Vehicle & {
      history: VehicleHistory[];
      breakHistory: VehicleBreakHistory[];
      status: VehicleStatus | null;
    })[];
    ghostVehicles: VehicleHistory[];
    tourSidebarActive?: boolean;
    currentTheme: string | null;
  };
}

const fitBoundsViewport = (coordinates: { lat: number; lng: number }[], width: number, height: number) => {
  // Creates an infinite loop downstream (in deckGL) if coordinates are not present (no error is thrown – false is returned instead)

  if (coordinates.length === 0) {
    return {
      latitude: 0,
      longitude: 0,
      zoom: 0,
      width,
      height,
      maxZoom: MAX_ZOOM_LEVEL,
      pitch: 0,
      bearing: 0,
    };
  }

  const coordinateBounds = getBounds(coordinates);

  const bounds = [
    coordinateBounds.min.lng,
    coordinateBounds.min.lat,
    coordinateBounds.max.lng,
    coordinateBounds.max.lat,
  ];

  const viewport = geoViewport.viewport(
    bounds,
    [Math.max(width - MAP_BOUNDS_PADDING * 2, 1), Math.max(height - MAP_BOUNDS_PADDING * 2, 1)],
    MIN_ZOOM_LEVEL,
    MAX_ZOOM_LEVEL,
    MAPBOX_TILE_SIZE,
  );

  return {
    latitude: viewport.center && viewport.center[1],
    longitude: viewport.center && viewport.center[0],
    zoom: viewport.zoom,
    width,
    height,
    maxZoom: MAX_ZOOM_LEVEL,
    pitch: 0,
    bearing: 0,
  };
};

type VehicleHistoryFilterMode = 'all' | 'tracking_ranges' | 'hide';
type VehicleBreakFilterMode = 'all' | 'tracking_ranges' | 'hide';

interface State {
  hideLayers: {
    history: Record<string, boolean>;
    geofences: boolean;
    events: boolean;
    externalEvents: boolean;
    historyPoints: boolean;
    tourArea: boolean;
  };
  vehicleHistoryFilterMode: Record<string, VehicleHistoryFilterMode>;
  vehicleBreakFilterMode: Record<string, VehicleBreakFilterMode>;
  hideCurrentPosition: Record<string, boolean>;
  rulers: Ruler[];
  isRulerEditMode: boolean;
  mapMode: 'map' | 'satellite';
}

enum MapLayerControls {
  HIDE_GEOFENCES = 'hide_geofences',
  HIDE_BREAKS = 'hide_breaks',
  HIDE_EVENTS = 'hide_events',
  HIDE_EXTERNAL_EVENTS = 'hide_external_events',
  HIDE_HISTORY_POINTS = 'hide_history_points',
  HIDE_TOUR_AREA = 'hide_tour_area',
}

export class TourMapView extends Component<TourMapViewDataProps, State> {
  constructor(props: TourMapViewDataProps) {
    super(props);

    this.state = {
      hideLayers: {
        geofences: false,
        history: {},
        events: false,
        externalEvents: false,
        historyPoints: false,
        tourArea: false,
      },
      vehicleHistoryFilterMode: {},
      vehicleBreakFilterMode: {},
      hideCurrentPosition: {},
      rulers: [],
      isRulerEditMode: false,
      mapMode: 'map',
    };
  }

  controlKeyDownListener = (ev: KeyboardEvent) => {
    if (ev.ctrlKey && ev.key === 'Backspace') {
      this.setState({ rulers: this.state.rulers.slice(0, -1) });
    }
    if (ev.key === 'Control') {
      this.setState({ isRulerEditMode: true });
    }
  };

  controlKeyUpListener = (ev: KeyboardEvent) => {
    if (ev.key === 'Control') {
      this.setState({ isRulerEditMode: false });
    }
  };

  componentDidMount() {
    window.addEventListener('keydown', this.controlKeyDownListener, false);
    window.addEventListener('keyup', this.controlKeyUpListener, false);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.controlKeyDownListener, false);
    window.removeEventListener('keyup', this.controlKeyUpListener, false);
  }

  handleMapControlCheckboxChange =
    (name: MapLayerControls) =>
    ({ checked }: { checked: boolean }) => {
      switch (name) {
        case MapLayerControls.HIDE_GEOFENCES:
          return this.setState({
            hideLayers: {
              ...this.state.hideLayers,
              geofences: !checked,
            },
          });
        case MapLayerControls.HIDE_EVENTS:
          return this.setState({
            hideLayers: {
              ...this.state.hideLayers,
              events: !checked,
            },
          });
        case MapLayerControls.HIDE_EXTERNAL_EVENTS:
          return this.setState({
            hideLayers: {
              ...this.state.hideLayers,
              externalEvents: !checked,
            },
          });
        case MapLayerControls.HIDE_HISTORY_POINTS:
          return this.setState({
            hideLayers: {
              ...this.state.hideLayers,
              historyPoints: !checked,
            },
          });
        case MapLayerControls.HIDE_TOUR_AREA:
          return this.setState({
            hideLayers: {
              ...this.state.hideLayers,
              tourArea: !checked,
            },
          });
        default:
          return;
      }
    };

  handleVehicleHistoryFilterModeChange = (vehicleId: string, mode: VehicleHistoryFilterMode) => {
    this.setState({
      vehicleHistoryFilterMode: {
        ...this.state.vehicleHistoryFilterMode,
        [vehicleId]: mode,
      },
    });
  };

  handleVehicleBreakFilterModeChange = (vehicleId: string, mode: VehicleBreakFilterMode) => {
    this.setState({
      vehicleBreakFilterMode: {
        ...this.state.vehicleBreakFilterMode,
        [vehicleId]: mode,
      },
    });
  };

  handleToggleCurrentPosition = (vehicleId: string, shouldHide: boolean) => {
    this.setState({
      hideCurrentPosition: {
        ...this.state.hideCurrentPosition,
        [vehicleId]: shouldHide,
      },
    });
  };

  handleAddRuler = (LngLat: [number, number]) => {
    const openEndedRuler = this.state.rulers.find((ruler) => ruler.toLngLat === undefined);
    if (openEndedRuler !== undefined) {
      return this.setState({
        rulers: [
          ...this.state.rulers.filter((ruler) => ruler.id !== openEndedRuler.id),
          {
            id: openEndedRuler.id,
            fromLngLat: openEndedRuler.fromLngLat,
            toLngLat: LngLat,
            distanceInMeters: calculateDistanceInMeters(openEndedRuler.fromLngLat, LngLat),
          },
        ],
      });
    }

    this.setState({
      rulers: [
        ...this.state.rulers,
        {
          id: generateRandomHexString(12),
          fromLngLat: LngLat,
        },
      ],
    });
  };

  resolveMapStyle = (): MapboxStyle => {
    if (this.state.mapMode === 'satellite') {
      return MapboxStyle.SATELLITE;
    }
    return getThemedMapStyle(this.props.data.currentTheme);
  };

  renderMapControls = (vehicles: Vehicle[]) => {
    const { hideLayers } = this.state;
    return (
      <Stack direction="row" gap="small" className="tour__map-controls">
        <Dropdown align="start">
          <DropdownButton kind="subtle">Marker Filters</DropdownButton>
          <DropdownMenu>
            <DropdownMenuItem key={MapLayerControls.HIDE_GEOFENCES} onClick={() => false}>
              <Checkbox
                label="Geofences"
                checked={!hideLayers.geofences}
                onChange={this.handleMapControlCheckboxChange(MapLayerControls.HIDE_GEOFENCES)}
              />
            </DropdownMenuItem>
            <DropdownMenuItem key={MapLayerControls.HIDE_EVENTS} onClick={() => false}>
              <Checkbox
                label="Events"
                checked={!hideLayers.events}
                onChange={this.handleMapControlCheckboxChange(MapLayerControls.HIDE_EVENTS)}
              />
            </DropdownMenuItem>
            <DropdownMenuItem key={MapLayerControls.HIDE_EXTERNAL_EVENTS} onClick={() => false}>
              <Checkbox
                label="External events"
                checked={!hideLayers.externalEvents}
                onChange={this.handleMapControlCheckboxChange(MapLayerControls.HIDE_EXTERNAL_EVENTS)}
              />
            </DropdownMenuItem>
            <DropdownMenuItem key={MapLayerControls.HIDE_HISTORY_POINTS} onClick={() => false}>
              <Checkbox
                label="History points"
                checked={!hideLayers.historyPoints}
                onChange={this.handleMapControlCheckboxChange(MapLayerControls.HIDE_HISTORY_POINTS)}
              />
            </DropdownMenuItem>
            <DropdownMenuItem key={MapLayerControls.HIDE_TOUR_AREA} onClick={() => false}>
              <Checkbox
                label="Tour area"
                checked={!hideLayers.tourArea}
                onChange={this.handleMapControlCheckboxChange(MapLayerControls.HIDE_TOUR_AREA)}
              />
            </DropdownMenuItem>
          </DropdownMenu>
        </Dropdown>
        <VehicleHistoryDropdown
          vehicles={vehicles}
          vehicleHistoryFilterMode={this.state.vehicleHistoryFilterMode}
          vehicleBreakFilterMode={this.state.vehicleBreakFilterMode}
          hideCurrentPosition={this.state.hideCurrentPosition}
          onHistoryFilterModeChange={this.handleVehicleHistoryFilterModeChange}
          onBreakFilterModeChange={this.handleVehicleBreakFilterModeChange}
          onToggleCurrentPosition={this.handleToggleCurrentPosition}
        />
        <Dropdown align="start">
          <DropdownButton kind="subtle">Map type</DropdownButton>
          <DropdownMenu>
            <DropdownMenuItem
              key="map"
              onClick={() => {
                this.setState({ mapMode: 'map' });
                return false;
              }}>
              <Radio value="map" label="Map" checked={this.state.mapMode === 'map'} />
            </DropdownMenuItem>
            <DropdownMenuItem
              key="satellite"
              onClick={() => {
                this.setState({ mapMode: 'satellite' });
                return false;
              }}>
              <Radio value="satellite" label="Satellite" checked={this.state.mapMode === 'satellite'} />
            </DropdownMenuItem>
          </DropdownMenu>
        </Dropdown>
      </Stack>
    );
  };

  render() {
    const { vehicles, tour, ghostVehicles, tourSidebarActive } = this.props.data;
    const config = getEmbedConfig();

    if (config === null) {
      throw Error('Missing config');
    }

    const overlayStops = getStopsFromTourStops(tour.stops).filter(
      (stop): stop is MappableStop => stop.lat !== null && stop.lng !== null,
    );
    const tourArea = tour.tour_area;
    const externalEvents = getExternalEvents(tour);

    const defaultViewportResolver = (width: number, height: number) => fitBoundsViewport(overlayStops, width, height);
    const route = { legs: tour.route?.legs ?? [] };
    const mapStyle = this.resolveMapStyle();

    return (
      <React.Fragment>
        {this.renderMapControls(vehicles)}
        <MapViewport
          style={{ backgroundColor: 'rgba(0,0,0,0.1)', minHeight: '100%' }}
          defaultViewport={defaultViewportResolver}
          tourSidebarActive={tourSidebarActive}>
          {({ width, height, viewport, onViewportChange }) => (
            <MapGL {...{ ...viewport, width, height }} mapStyle={mapStyle} mapboxApiAccessToken={config.mapbox_token}>
              <TourOverlay
                layersToHide={this.state.hideLayers}
                shouldDisplayCrosshairCursor={this.state.isRulerEditMode}
                viewport={{ ...viewport, width, height }}
                onViewportChange={onViewportChange}
                onAddRuler={(lngLat) => this.handleAddRuler(lngLat)}
                stops={overlayStops}
                tourArea={tourArea}
                rulers={this.state.rulers}
                events={tour.events}
                externalEvents={externalEvents}
                route={route}
                vehicles={vehicles}
                ghostVehicles={ghostVehicles}
                trackingSegments={tour.trackingSegments}
                vehicleHistoryFilterMode={this.state.vehicleHistoryFilterMode}
                vehicleBreakFilterMode={this.state.vehicleBreakFilterMode}
                hideCurrentPosition={this.state.hideCurrentPosition}
                onLayerClick={(coordinates: { lat: number; lng: number }) => {
                  if (this.state.isRulerEditMode) {
                    return;
                  }
                  openSatelliteViewUrl(coordinates);
                  return;
                }}
                mapStyle={mapStyle}
              />
            </MapGL>
          )}
        </MapViewport>
      </React.Fragment>
    );
  }
}

interface VehicleHistoryDropdownProps {
  vehicles: Vehicle[];
  vehicleHistoryFilterMode: Record<string, VehicleHistoryFilterMode>;
  vehicleBreakFilterMode: Record<string, VehicleBreakFilterMode>;
  hideCurrentPosition: Record<string, boolean>;
  onHistoryFilterModeChange: (vehicleId: string, mode: VehicleHistoryFilterMode) => void;
  onBreakFilterModeChange: (vehicleId: string, mode: VehicleBreakFilterMode) => void;
  onToggleCurrentPosition: (vehicleId: string, shouldHide: boolean) => void;
}

const VehicleHistoryDropdown: React.FC<VehicleHistoryDropdownProps> = ({
  vehicles,
  vehicleHistoryFilterMode,
  vehicleBreakFilterMode,
  hideCurrentPosition,
  onHistoryFilterModeChange,
  onBreakFilterModeChange,
  onToggleCurrentPosition,
}) => {
  const tooltipContent = (
    <div style={{ minWidth: '600px', maxWidth: '750px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-neutral-border-subtle)' }}>
            <th
              rowSpan={2}
              style={{
                textAlign: 'left',
                fontWeight: 'bold',
                fontSize: '11px',
                paddingBottom: '8px',
                verticalAlign: 'bottom',
              }}>
              Vehicle
            </th>
            <th
              colSpan={3}
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '12px',
                paddingBottom: '3px',
                paddingLeft: '5px',
                paddingRight: '5px',
              }}>
              History
            </th>
            <th rowSpan={2} style={{ width: '20px', padding: 0 }}></th>
            <th
              colSpan={3}
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '12px',
                paddingBottom: '3px',
                paddingLeft: '5px',
                paddingRight: '5px',
              }}>
              Breaks
            </th>
            <th rowSpan={2} style={{ width: '20px', padding: 0 }}></th>
            <th
              rowSpan={2}
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '11px',
                paddingBottom: '8px',
                verticalAlign: 'bottom',
              }}>
              Current pos
            </th>
          </tr>
          <tr>
            <th
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '11px',
                paddingTop: '5px',
                paddingBottom: '8px',
                width: '50px',
              }}>
              All
            </th>
            <th
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '11px',
                paddingTop: '5px',
                paddingBottom: '8px',
                width: '50px',
              }}>
              Ranges
            </th>
            <th
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '11px',
                paddingTop: '5px',
                paddingBottom: '8px',
                width: '50px',
              }}>
              Hide
            </th>
            <th
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '11px',
                paddingTop: '5px',
                paddingBottom: '8px',
                width: '50px',
              }}>
              All
            </th>
            <th
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '11px',
                paddingTop: '5px',
                paddingBottom: '8px',
                width: '50px',
              }}>
              Ranges
            </th>
            <th
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '11px',
                paddingTop: '5px',
                paddingBottom: '8px',
                width: '50px',
              }}>
              Hide
            </th>
          </tr>
        </thead>
        <tbody>
          {vehicles.map((vehicle) => {
            const historyMode = vehicleHistoryFilterMode[vehicle.vehicle_id] || 'all';
            const breakMode = vehicleBreakFilterMode[vehicle.vehicle_id] || 'all';
            const isCurrentPositionHidden = hideCurrentPosition[vehicle.vehicle_id] ?? true;
            return (
              <tr key={vehicle.vehicle_id} style={{ borderBottom: '1px solid var(--color-neutral-border-subtle)' }}>
                <td
                  style={{
                    padding: '5px 0',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '150px',
                  }}>
                  {vehicle.license_plate_number ?? `ID: ${vehicle.vehicle_id}`}
                </td>
                <td style={{ textAlign: 'center', padding: '5px 0' }}>
                  <Radio
                    value="all"
                    label=""
                    checked={historyMode === 'all'}
                    onChange={() => onHistoryFilterModeChange(vehicle.vehicle_id, 'all')}
                  />
                </td>
                <td style={{ textAlign: 'center', padding: '5px 0' }}>
                  <Radio
                    value="tracking_ranges"
                    label=""
                    checked={historyMode === 'tracking_ranges'}
                    onChange={() => onHistoryFilterModeChange(vehicle.vehicle_id, 'tracking_ranges')}
                  />
                </td>
                <td style={{ textAlign: 'center', padding: '5px 0' }}>
                  <Radio
                    value="hide"
                    label=""
                    checked={historyMode === 'hide'}
                    onChange={() => onHistoryFilterModeChange(vehicle.vehicle_id, 'hide')}
                  />
                </td>
                <td style={{ padding: 0, textAlign: 'center' }}>
                  <div
                    style={{
                      width: '2px',
                      height: '100%',
                      backgroundColor: 'var(--color-neutral-border-subtle)',
                      margin: '0 auto',
                    }}></div>
                </td>
                <td style={{ textAlign: 'center', padding: '5px 0' }}>
                  <Radio
                    value="all"
                    label=""
                    checked={breakMode === 'all'}
                    onChange={() => onBreakFilterModeChange(vehicle.vehicle_id, 'all')}
                  />
                </td>
                <td style={{ textAlign: 'center', padding: '5px 0' }}>
                  <Radio
                    value="tracking_ranges"
                    label=""
                    checked={breakMode === 'tracking_ranges'}
                    onChange={() => onBreakFilterModeChange(vehicle.vehicle_id, 'tracking_ranges')}
                  />
                </td>
                <td style={{ textAlign: 'center', padding: '5px 0' }}>
                  <Radio
                    value="hide"
                    label=""
                    checked={breakMode === 'hide'}
                    onChange={() => onBreakFilterModeChange(vehicle.vehicle_id, 'hide')}
                  />
                </td>
                <td style={{ padding: 0, textAlign: 'center' }}>
                  <div
                    style={{
                      width: '2px',
                      height: '100%',
                      backgroundColor: 'var(--color-neutral-border-subtle)',
                      margin: '0 auto',
                    }}></div>
                </td>
                <td style={{ padding: '5px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Toggle
                      label="Current position"
                      hideLabel
                      checked={!isCurrentPositionHidden}
                      onChange={() => onToggleCurrentPosition(vehicle.vehicle_id, !isCurrentPositionHidden)}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <Popover content={tooltipContent} maxWidth={750} placement="bottom">
      <Button kind="subtle">Vehicle History & Breaks</Button>
    </Popover>
  );
};
